import { CornerDownRight } from 'lucide-react';
import type { TeamPerformanceRow } from '../../services/analytics.service';
import { analyticsService } from '../../services/analytics.service';

interface TeamPerformanceTableProps {
  rows: TeamPerformanceRow[];
  isLoading: boolean;
}

/** Parents first (by name), each followed by its sub-teams; standalone teams in between. */
export function orderTeamRows(rows: TeamPerformanceRow[]): Array<TeamPerformanceRow & { depth: 0 | 1 }> {
  const ids = new Set(rows.map((r) => r.teamId));
  const roots = rows
    .filter((r) => !r.parentTeamId || !ids.has(r.parentTeamId))
    .sort((a, b) => a.name.localeCompare(b.name));
  return roots.flatMap((root) => [
    { ...root, depth: 0 as const },
    ...rows
      .filter((r) => r.parentTeamId === root.teamId)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((r) => ({ ...r, depth: 1 as const })),
  ]);
}

const pct = (v: number | null) => (v === null ? '–' : `${Math.round(v)}%`);

/**
 * Reports → Team Performance. Each team's figures cover its agents; a parent
 * covers the agents of all its sub-teams, each counted once.
 */
export function TeamPerformanceTable({ rows, isLoading }: TeamPerformanceTableProps) {
  const ordered = orderTeamRows(rows);
  return (
    <div className="rounded-[14px] border border-[var(--co-line)] bg-[var(--co-raised)]" data-testid="team-performance">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--co-line)] text-left text-xs uppercase tracking-wide text-[var(--co-ink-3)]">
              <th className="px-4 py-3 font-semibold">Team</th>
              <th className="px-4 py-3 font-semibold">Agents</th>
              <th className="px-4 py-3 font-semibold">Tickets</th>
              <th className="px-4 py-3 font-semibold">Resolved</th>
              <th className="px-4 py-3 font-semibold">SLA</th>
              <th className="px-4 py-3 font-semibold">Avg first response</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-[var(--co-ink-3)]">
                  Loading team performance…
                </td>
              </tr>
            ) : ordered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-[var(--co-ink-3)]">
                  No teams to show.
                </td>
              </tr>
            ) : (
              ordered.map((row) => (
                <tr key={row.teamId} className="border-b border-[var(--co-line)] last:border-0">
                  <td className="px-4 py-3 text-[var(--co-ink)]">
                    <span className={row.depth === 1 ? 'flex items-center gap-1.5 pl-5' : 'font-medium'}>
                      {row.depth === 1 && <CornerDownRight className="h-3.5 w-3.5 text-[var(--co-ink-4)]" />}
                      {row.name}
                    </span>
                  </td>
                  <td className="px-4 py-3">{row.agentCount}</td>
                  <td className="px-4 py-3">{row.ticketsAssigned}</td>
                  <td className="px-4 py-3">{row.ticketsResolved}</td>
                  <td className="px-4 py-3">{pct(row.slaCompliance)}</td>
                  <td className="px-4 py-3">
                    {row.avgResponseTime === null ? '–' : analyticsService.formatDuration(row.avgResponseTime)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <p className="border-t border-[var(--co-line)] px-4 py-2 text-xs text-[var(--co-ink-3)]">
        A team's figures cover its agents; a parent team covers the agents of all its sub-teams, each
        counted once. An agent in several teams counts under each of them.
      </p>
    </div>
  );
}
